import {
	ref, computed, Ref, ComputedRef,
} from 'vue';
import { Callback, EventEmitter } from '../../../shared/models/EventEmitter';
import { clamp } from '../../../shared/utilities/helpers';

export interface ProgressLoader {
	status: Ref<number | null>;
	percent: ComputedRef<number>;
	segments: Readonly<number[]>;
	increase(amount?: number): number;
	finish(segmendId: number): void;
	finishAll(): void;
	start(segmendId?: number): number;
	onStart(callback: Callback): void;
	onFinish(callback: Callback): void;
	onIncrease(callback: Callback): void;
}

export interface ProgressLoaderOptions {
	resetAfterFinish?: boolean;
}

export const useProgressLoader = (options?: ProgressLoaderOptions): ProgressLoader => {
	const segments: number[] = [];
	const eventEmitter = new EventEmitter();
	const speed = 100;

	let finishTimeout: ReturnType<typeof setTimeout> | null = null;
	let resetTimeout: ReturnType<typeof setTimeout> | null = null;
	let updateInterval: ReturnType<typeof setInterval> | null = null;

	const status = ref<number | null>(null);

	const percent = computed(() => {
		if (typeof status.value !== 'number') {
			return 0;
		}

		return 100 * status.value;
	});

	const addSegment = (segmentId: number) => {
		segments.push(segmentId);
	};

	const removeSegment = (segmentId: number) => {
		segments.splice(segments.indexOf(segmentId), 1);
	};

	const reset = () => {
		status.value = null;
	};

	const increase = (amount?: number): number => {
		let newAmount = amount;

		if (segments.length === 0) {
			return 0;
		}

		if (typeof status.value !== 'number') {
			return 0;
		}

		if (status.value > 1) {
			return 0;
		}

		if (!newAmount) {
			if (status.value >= 0 && status.value < 0.2) {
				newAmount = 0.05;
			} else if (status.value >= 0.2 && status.value < 0.5) {
				newAmount = 0.02;
			} else if (status.value >= 0.5 && status.value < 0.8) {
				newAmount = 0.01;
			} else if (status.value >= 0.8 && status.value < 0.99) {
				newAmount = 0.002;
			} else {
				newAmount = 0;
			}
		}

		const newStatus = clamp(status.value + newAmount, 0, 0.994);

		status.value = newStatus;

		eventEmitter.emit('increase', newStatus);

		return newStatus;
	};

	const start = (segmentId?: number) => {
		const newSegmentId = segmentId || Date.now();

		if (segments.length === 0) {
			eventEmitter.emit('start');
		}

		addSegment(newSegmentId);

		if (updateInterval) {
			clearInterval(updateInterval);
		}

		if (resetTimeout) {
			clearTimeout(resetTimeout);
		}

		if (typeof status.value !== 'number' || status.value === 1) {
			status.value = 0;
		}

		updateInterval = setInterval(() => {
			increase();
		}, speed);

		return newSegmentId;
	};

	const setFinishTimeout = () => {
		if (finishTimeout) {
			clearTimeout(finishTimeout);
		}

		finishTimeout = setTimeout(() => {
			if (segments.length > 0) {
				increase(0.1);
				return;
			}

			if (updateInterval) {
				clearInterval(updateInterval);
			}

			status.value = 1;
			eventEmitter.emit('finish');

			if (options?.resetAfterFinish) {
				if (resetTimeout) {
					clearTimeout(resetTimeout);
				}

				resetTimeout = setTimeout(() => {
					reset();
				}, 200);
			}
		}, 250);
	};

	const finish = (segmentId: number) => {
		removeSegment(segmentId);
		setFinishTimeout();
	};

	const finishAll = () => {
		segments.splice(0, segments.length);
		setFinishTimeout();
	};

	const onStart = (callback: Callback): void => {
		eventEmitter.on('start', callback);
	};

	const onIncrease = (callback: Callback): void => {
		eventEmitter.on('increase', callback);
	};

	const onFinish = (callback: Callback): void => {
		eventEmitter.on('finish', callback);
	};

	return {
		status,
		percent,
		segments,
		start,
		finish,
		finishAll,
		increase,
		onStart,
		onIncrease,
		onFinish,
	};
};
